<template>
    <div class="top-player-card" :class="{ 'is-current-user': player.is_current_user }">
        <div class="player-photo-section">
            <div class="player-photo-wrapper">
                <img v-if="player.photo_url" :src="player.photo_url" :alt="player.full_name" class="player-photo"
                    @error="handleImageError" />
                <div v-else class="photo-placeholder">
                    {{ getInitials(player.full_name) }}
                </div>
                <div v-if="player.is_current_user" class="current-user-badge">Вы</div>
                <div v-if="player.position_slug" class="position-badge" :title="player.position">
                    {{ getPositionAbbreviation(player.position_slug) }}
                </div>
                <div v-if="player.number" class="player-number-badge">#{{ player.number }}</div>
                <a
                    v-if="teamPublicPath(player)"
                    :href="teamPublicPath(player)"
                    class="team-logo-badge-link"
                    @click.stop
                >
                    <img :src="player.team_logo" :alt="player.team_name" class="team-logo-badge" />
                </a>
                <img
                    v-else-if="player.team_logo"
                    :src="player.team_logo"
                    :alt="player.team_name"
                    class="team-logo-badge"
                />
            </div>
        </div>

        <div class="player-info-section">
            <h4 class="player-name">
                <RegisteredBadge :status="player.status" />
                <a v-if="player.url" :href="player.url" class="player-name-link">{{ player.full_name }}</a>
                <span v-else class="player-name-text">{{ player.full_name }}</span>
            </h4>

            <div class="player-stats">
                <div class="stat-item">
                    <span class="stat-label">Матчей:</span>
                    <span class="stat-value">{{ player.matches_played }}</span>
                </div>

                <div class="stat-item">
                    <span class="stat-label">Партий:</span>
                    <span class="stat-value">{{ player.sets_played || player.matches_played }}</span>
                </div>

                <div v-if="category === 'attack' && player.attack_kills_per_set !== undefined" class="stat-item">
                    <span class="stat-label">Очков за сет:</span>
                    <span class="stat-value">{{ formatKillsPerSet(player.attack_kills_per_set) }}</span>
                </div>

                <div v-if="category === 'serve' && player.serve_aces_per_set !== undefined" class="stat-item">
                    <span class="stat-label">Очков за сет:</span>
                    <span class="stat-value">{{ formatKillsPerSet(player.serve_aces_per_set) }}</span>
                </div>

                <div v-if="category === 'block' && player.block_positive_per_set !== undefined" class="stat-item">
                    <span class="stat-label">Очков за сет:</span>
                    <span class="stat-value">{{ formatKillsPerSet(player.block_positive_per_set) }}</span>
                </div>

                <div v-if="category === 'reception' && player.reception_total" class="stat-item">
                    <span class="stat-label">Позитивный прием:</span>
                    <span class="stat-value">{{ formatEfficiency(player.reception_positive_pct) }}%</span>
                </div>

                <!-- Статистика в зависимости от категории -->
                <template v-if="category === 'attack'">
                    <EfficiencyFormula :player="player" :category="category" theme="dark" />
                </template>

                <template v-else-if="category === 'serve'">
                    <EfficiencyFormula :player="player" :category="category" theme="dark" />
                </template>

                <template v-else-if="category === 'block'">
                    <EfficiencyFormula :player="player" :category="category" theme="dark" />
                </template>

                <template v-else-if="category === 'reception'">
                    <EfficiencyFormula :player="player" :category="category" theme="dark" />
                </template>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref } from 'vue';
import EfficiencyFormula from './EfficiencyFormula.vue';
import RegisteredBadge from '@/Components/shared/RegisteredBadge.vue';
import { teamPublicPath } from '@/utils/teamUrl';

const props = defineProps({
    player: {
        type: Object,
        required: true
    },
    category: {
        type: String,
        required: true
    },
    sortBy: {
        type: String,
        default: 'kills'
    }
});

const imageError = ref(false);

const handleImageError = () => {
    imageError.value = true;
};

const getInitials = (fullName) => {
    if (!fullName) return 'Т';
    const parts = fullName.split(' ');
    if (parts.length >= 2) {
        return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
    }
    return fullName.charAt(0).toUpperCase();
};

const formatKillsPerSet = (value) => {
    if (value === null || value === undefined) return '0.00';
    return Number(value).toFixed(2);
};

const formatEfficiency = (value) => {
    if (value === null || value === undefined) return '0.00';
    return Number(value).toFixed(2);
};

const getPercentage = (value, total) => {
    if (!total || total === 0) return '0';
    return Number((value / total) * 100).toFixed(1);
};

const getPositionAbbreviation = (positionSlug) => {
    const abbreviations = {
        'setter': 'S',
        'outside_hitter': 'OH',
        'opposite': 'O',
        'middle_blocker': 'MB',
        'libero': 'L'
    };
    return abbreviations[positionSlug] || positionSlug?.charAt(0).toUpperCase() || '';
};
</script>

<style lang="scss" scoped>
@import '../../../scss/tokens';

.top-player-card {
    background: linear-gradient(135deg, $color-brand-dark 0%, $color-brand 52%, $color-brand-light 100%);
    border-radius: $radius-md;
    padding: 1.25rem;
    color: #fff;
    display: flex;
    flex-direction: column;
    gap: 1rem;
    position: relative;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    backdrop-filter: blur(10px);

    &.is-current-user {
        border: 2px solid #ffd700;
        box-shadow: 0 2px 8px rgba(255, 215, 0, 0.3);
    }

    .player-photo-section {
        display: flex;
        justify-content: center;

        .player-photo-wrapper {
            position: relative;
            width: 80px;
            height: 80px;

            &:hover {

                .position-badge,
                .player-number-badge,
                .team-logo-badge,
                .team-logo-badge-link {
                    opacity: 0;
                    transition: opacity 0.2s ease;
                }
            }

            .player-photo {
                width: 100%;
                height: 100%;
                border-radius: 8px;
                @include person-photo-cover;
                border: 3px solid rgba(255, 255, 255, 0.3);
            }

            .photo-placeholder {
                width: 100%;
                height: 100%;
                border-radius: 8px;
                background: rgba(255, 255, 255, 0.2);
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 1.5rem;
                font-weight: 600;
                border: 3px solid rgba(255, 255, 255, 0.3);
            }

            .current-user-badge {
                position: absolute;
                top: -6px;
                right: -6px;
                background: #ffd700;
                color: #1a1a1a;
                padding: 0.2rem 0.5rem;
                border-radius: 8px;
                font-size: 0.45rem;
                font-weight: 700;
                box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
                z-index: 2;
            }

            .position-badge {
                position: absolute;
                top: -4px;
                right: -4px;
                background: rgba(255, 255, 255, 0.95);
                color: #1a1a1a;
                padding: 0.2rem 0.4rem;
                border-radius: 6px;
                font-size: 0.5rem;
                font-weight: 600;
                box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
                backdrop-filter: blur(10px);
                cursor: help;
                z-index: 1;
                transition: opacity 0.2s ease;
            }

            .player-number-badge {
                position: absolute;
                bottom: -4px;
                right: -4px;
                background: rgba(255, 255, 255, 0.95);
                color: #1a1a1a;
                padding: 0.25rem 0.5rem;
                border-radius: 6px;
                font-size: 0.5rem;
                font-weight: 600;
                box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
                backdrop-filter: blur(10px);
                transition: opacity 0.2s ease;
            }

            .team-logo-badge-link {
                position: absolute;
                bottom: -4px;
                left: -4px;
                z-index: 1;
                line-height: 0;
                transition: opacity 0.2s ease;
            }

            .team-logo-badge {
                position: absolute;
                bottom: -4px;
                left: -4px;
                width: 24px;
                height: 24px;
                object-fit: contain;
                background: rgba(255, 255, 255, 0.95);
                padding: 0.15rem;
                border-radius: 6px;
                box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
                backdrop-filter: blur(10px);
            }

            .team-logo-badge-link .team-logo-badge {
                position: static;
            }
        }
    }

    .player-info-section {
        text-align: center;

        .player-name {
            font-weight: 700;
            margin-bottom: 1rem;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 0.5rem;
            max-width: 100%;
            min-width: 0;

            .player-name-link {
                color: #fff;
                text-decoration: underline;
                cursor: pointer;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                max-width: 100%;
                min-width: 0;

                &:hover {
                    color: #e0e0e0;
                    text-decoration: underline;
                }
            }

            .player-name-text {
                color: #fff;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                max-width: 100%;
                min-width: 0;
            }
        }

        .player-stats {
            display: flex;
            flex-wrap: wrap;
            gap: 0.6rem;
            justify-content: center;

            .stat-item {
                display: flex;
                flex-direction: column;
                gap: 0.2rem;
                padding: 0.5rem;
                background: rgba(255, 255, 255, 0.1);
                border-radius: 6px;
                border: 1px solid #ffffff;
                flex: 1 1 calc(33.333% - 0.6rem);
                min-width: 0;

                &.highlight {
                    background: rgba(255, 255, 255, 0.2);
                    border: 1.5px solid rgba(255, 255, 255, 0.3);
                }

                &.stat-item-block-full {
                    padding: 0.75rem;
                    margin-top: 0.5rem;
                    justify-content: center;
                    height: 5.5rem;
                    display: flex;
                    flex-direction: column;
                    align-items: center;

                }

                .stat-label {
                    font-size: 0.8rem;
                    font-weight: 700;
                }

                .stat-value {
                    font-size: 0.9rem;
                    font-weight: 700;
                }

                .stat-formula-mini {
                    display: flex;
                    align-items: center;
                    gap: 0.2rem;
                    flex-wrap: wrap;
                    margin-top: 0.3rem;

                    .formula-box-mini {
                        display: inline-flex;
                        align-items: center;
                        justify-content: center;
                        min-width: 1.5rem;
                        padding: 0.15rem 0.3rem;
                        border-radius: 3px;
                        font-size: 0.6rem;
                        font-weight: 600;
                        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
                        color: rgba(255, 255, 255, 0.95);

                        &.formula-kills {
                            background: #77c144;
                            border: 1px solid rgba(255, 255, 255, 0.2);
                        }

                        &.formula-positive {
                            background: hsl(0deg 0.76% 55.57% / 60%);
                            border: 1px solid rgba(255, 255, 255, 0.2);
                        }

                        &.formula-total {
                            background: hsl(0deg 0.76% 55.57% / 60%);
                            border: 1px solid rgba(255, 255, 255, 0.2);
                        }
                    }

                    .formula-text-mini {
                        font-size: 0.6rem;
                        color: rgba(255, 255, 255, 0.9);
                        white-space: nowrap;
                    }

                    .formula-value-mini {
                        font-size: 0.7rem;
                        font-weight: 700;
                        color: rgba(255, 255, 255, 0.95);
                        margin-left: 0.2rem;
                    }
                }

                &.formula-item {
                    flex: 1 1 100%;
                }
            }
        }
    }
}
</style>
